<template>
  <!-- Panel de propiedades vertical a la izquierda. PRESENTACIÓN PURA: lee los valores
       por props y sube los cambios con eventos update:* / z-index (el padre decide si
       aplican al dibujo nuevo o al elemento seleccionado). No conoce el motor ni la api.
       `mode`: 'vector' | 'text'. `editing`: true cuando hay un elemento seleccionado. -->
  <div class="epc-props-panel">

    <!-- ===================== VECTOR ===================== -->
    <template v-if="mode === 'vector'">
      <!-- Trazo (color) -->
      <div class="epc-props-section">
        <span class="epc-props-label">{{ $t('stroke') }}</span>
        <ViewerColorPicker :value="strokeColor" :label="$t('stroke')"
          :picks="STROKE_COLORS" :palette="STROKE_PALETTE"
          @input="$emit('update:strokeColor', $event)" />
      </div>

      <!-- Fondo (relleno) -->
      <div class="epc-props-section">
        <span class="epc-props-label">{{ $t('fill') }}</span>
        <ViewerColorPicker :value="fillColor" :label="$t('fill')"
          :picks="FILL_COLORS" :palette="FILL_PALETTE"
          @input="$emit('update:fillColor', $event)" />
      </div>

      <!-- Grosor del trazo -->
      <div class="epc-props-section">
        <span class="epc-props-label">{{ $t('stroke_width') }}</span>
        <div class="epc-btn-row">
          <button v-for="w in STROKE_WIDTH_PRESETS" :key="'sw_' + w.key" class="epc-prop-btn"
            :class="{ active: strokeWidth === w.value }" @click="$emit('update:strokeWidth', w.value)">
            <span class="epc-stroke-preview" :style="{ height: (w.value + 0.5) + 'px' }"></span>
          </button>
        </div>
      </div>

      <!-- Estilo del trazo (sólido / guiones / puntos) -->
      <div class="epc-props-section">
        <span class="epc-props-label">{{ $t('stroke_style') }}</span>
        <div class="epc-btn-row">
          <button v-for="t in DASH_TYPES" :key="'ds_' + t" class="epc-prop-btn"
            :class="{ active: strokeStyle === t }" @click="$emit('update:strokeStyle', t)">
            <span class="epc-dash-preview" :class="'is-' + t"></span>
          </button>
        </div>
      </div>

      <!-- Bordes (afilado / redondo) -->
      <div class="epc-props-section">
        <span class="epc-props-label">{{ $t('edges') }}</span>
        <div class="epc-btn-row">
          <button v-for="e in EDGES" :key="'ed_' + e" class="epc-prop-btn"
            :class="{ active: edges === e }" :title="$t(e)" @click="$emit('update:edges', e)">
            <b-icon :icon="e === 'round' ? 'circle-notch' : 'square'" pack="fas" size="is-small"></b-icon>
          </button>
        </div>
      </div>

      <!-- Opacidad -->
      <div class="epc-props-section">
        <span class="epc-props-label">{{ $t('opacity') }}</span>
        <b-slider :value="opacityPercent" :min="0" :max="100" :step="10" size="is-small"
          :tooltip="false" @input="$emit('update:opacity', $event / 100)"></b-slider>
      </div>

      <!-- Capas (z-index) -->
      <div v-if="editing" class="epc-props-section">
        <span class="epc-props-label">{{ $t('layers') }}</span>
        <div class="epc-btn-row">
          <button v-for="z in Z_INDEX_ACTIONS" :key="'z_' + z.dir" class="epc-prop-btn"
            :title="$t(z.key)" @click="$emit('z-index', z.dir)">
            <b-icon :icon="z.icon" pack="fas" size="is-small"></b-icon>
          </button>
        </div>
      </div>

      <!-- Acciones (solo al editar un elemento existente) -->
      <div v-if="editing" class="epc-props-section">
        <span class="epc-props-label">{{ $t('actions') }}</span>
        <div class="epc-btn-row">
          <button class="epc-prop-btn is-danger" @click="$emit('request-delete')">
            <b-icon icon="trash" pack="fas" size="is-small"></b-icon>
          </button>
        </div>
      </div>
    </template>

    <!-- ===================== TEXTO ===================== -->
    <!-- El menú se muestra directo al armar la herramienta: los valores sirven de
         DEFAULTS para el texto que se coloque; al seleccionar un texto, lo editan. -->
    <template v-else-if="mode === 'text'">
      <!-- Color del texto -->
      <div class="epc-props-section">
        <span class="epc-props-label">{{ $t('stroke') }}</span>
        <ViewerColorPicker :value="textColor" :label="$t('stroke')"
          :picks="STROKE_COLORS" :palette="STROKE_PALETTE"
          @input="$emit('update:textColor', $event)" />
      </div>

      <!-- Tamaño de la fuente (S/M/L/XL) -->
      <div class="epc-props-section">
        <span class="epc-props-label">{{ $t('font_size') }}</span>
        <div class="epc-btn-row">
          <button v-for="s in FONT_SIZE_PRESETS" :key="'fs_' + s.key" class="epc-prop-btn"
            :class="{ active: fontSize === s.value }" @click="$emit('update:fontSize', s.value)">
            <span class="epc-size-label">{{ s.key }}</span>
          </button>
        </div>
      </div>

      <!-- Negrita / Cursiva -->
      <div class="epc-props-section">
        <span class="epc-props-label">{{ $t('font_weight') }}</span>
        <div class="epc-btn-row">
          <button class="epc-prop-btn" :class="{ active: bold }" :title="$t('bold')"
            @click="$emit('update:bold', !bold)">
            <b-icon icon="bold" pack="fas" size="is-small"></b-icon>
          </button>
          <button class="epc-prop-btn" :class="{ active: italic }" :title="$t('italic')"
            @click="$emit('update:italic', !italic)">
            <b-icon icon="italic" pack="fas" size="is-small"></b-icon>
          </button>
        </div>
      </div>

      <!-- Alineado de texto -->
      <div class="epc-props-section">
        <span class="epc-props-label">{{ $t('text_align') }}</span>
        <div class="epc-btn-row">
          <button v-for="a in TEXT_ALIGNS" :key="'al_' + a" class="epc-prop-btn"
            :class="{ active: align === a }" @click="$emit('update:align', a)">
            <b-icon :icon="'align-' + a" pack="fas" size="is-small"></b-icon>
          </button>
        </div>
      </div>

      <!-- Opacidad -->
      <div class="epc-props-section">
        <span class="epc-props-label">{{ $t('opacity') }}</span>
        <b-slider :value="opacityPercent" :min="0" :max="100" :step="10" size="is-small"
          :tooltip="false" @input="$emit('update:opacity', $event / 100)"></b-slider>
      </div>

      <!-- Capas (z-index) -->
      <div v-if="editing" class="epc-props-section">
        <span class="epc-props-label">{{ $t('layers') }}</span>
        <div class="epc-btn-row">
          <button v-for="z in Z_INDEX_ACTIONS" :key="'tz_' + z.dir" class="epc-prop-btn"
            :title="$t(z.key)" @click="$emit('z-index', z.dir)">
            <b-icon :icon="z.icon" pack="fas" size="is-small"></b-icon>
          </button>
        </div>
      </div>

      <!-- Acciones (solo al editar) -->
      <div v-if="editing" class="epc-props-section">
        <span class="epc-props-label">{{ $t('actions') }}</span>
        <div class="epc-btn-row">
          <button class="epc-prop-btn is-danger" @click="$emit('request-delete')">
            <b-icon icon="trash" pack="fas" size="is-small"></b-icon>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  STROKE_COLORS, FILL_COLORS, STROKE_PALETTE, FILL_PALETTE,
  STROKE_WIDTH_PRESETS, DASH_TYPES, EDGES, Z_INDEX_ACTIONS,
  FONT_SIZE_PRESETS, TEXT_ALIGNS,
} from '../../data/tool-options'
import ViewerColorPicker from './ViewerColorPicker.vue'

export default {
  name: 'ViewerPropertiesPanel',

  components: { ViewerColorPicker },

  props: {
    mode: { type: String, default: null }, // 'vector' | 'text'
    editing: { type: Boolean, default: false },
    // Vector
    strokeColor: { type: String, default: '#1e1e1e' },
    fillColor: { type: String, default: 'transparent' },
    strokeWidth: { type: Number, default: 1 },
    strokeStyle: { type: String, default: 'solid' },
    edges: { type: String, default: 'sharp' },
    opacity: { type: Number, default: 1 }, // 0..1
    // Texto
    textColor: { type: String, default: '#1e1e1e' },
    fontSize: { type: Number, default: 16 },
    bold: { type: Boolean, default: false },
    italic: { type: Boolean, default: false },
    align: { type: String, default: 'left' },
  },

  data() {
    return {
      STROKE_COLORS, FILL_COLORS, STROKE_PALETTE, FILL_PALETTE,
      STROKE_WIDTH_PRESETS, DASH_TYPES, EDGES, Z_INDEX_ACTIONS,
      FONT_SIZE_PRESETS, TEXT_ALIGNS,
    }
  },

  computed: {
    opacityPercent() {
      return Math.round((this.opacity != null ? this.opacity : 1) * 100)
    },
  },
}
</script>

<style scoped>
.epc-props-panel {
  position: absolute;
  left: 10px;
  top: 60px;
  z-index: 6;
  width: 200px;
  background-color: #ffffff;
  border: 1px solid #e9e9ef;
  border-radius: 12px;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.12);
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: calc(100vh - 80px);
  overflow-y: auto;
}

.epc-props-hint {
  font-size: 12px;
  color: #868e96;
  margin: 0;
}

.epc-props-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.epc-props-label {
  font-size: 12px;
  color: #495057;
}

.epc-btn-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
}

/* Botones de propiedad (grosor / estilo / tamaño / bordes / capas / acciones) */
.epc-prop-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  height: 34px;
  padding: 0 8px;
  border-radius: 8px;
  border: 1px solid transparent;
  background-color: #f4f4f6;
  color: #495057;
  cursor: pointer;
  transition: background-color 0.12s ease, color 0.12s ease;
}

.epc-prop-btn:hover {
  background-color: #ececf1;
}

.epc-prop-btn.active {
  background-color: #e8eef8;
  color: #3a6bc8;
}

.epc-prop-btn.is-danger {
  color: #e03131;
}

.epc-prop-btn.is-danger:hover {
  background-color: #ffe3e3;
}

/* Vista previa del grosor */
.epc-stroke-preview {
  display: block;
  width: 16px;
  min-height: 1px;
  background-color: currentColor;
  border-radius: 2px;
}

/* Vista previa del estilo de línea */
.epc-dash-preview {
  display: block;
  width: 18px;
  height: 0;
  border-top: 2px solid currentColor;
}

.epc-dash-preview.is-dashed {
  border-top-style: dashed;
}

.epc-dash-preview.is-dotted {
  border-top-style: dotted;
}

.epc-size-label {
  font-size: 12px;
  font-weight: 600;
}
</style>
